import React, { useEffect, useState, useRef } from 'react';
import Axios from 'axios';
import { toast } from 'react-toastify';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Typography,
  Form,
  Input,
  Select,
  TreeSelect,
  Button,
  Row,
  Col,
  Space,
  Tooltip,
  DatePicker,
  InputNumber,
  Switch,
  Table,
  Card,
  Tag,
} from 'antd';
import { EyeOutlined, PlusOutlined, RetweetOutlined } from '@ant-design/icons';
import { Factory, Package, Check } from 'lucide-react';
import {
  bp_batch_configuration,
  get_master_batchs,
  batch_configuration_combine,
  get_recipe_for_schedule,
  get_product_list_both,
  get_api_function,
  get_recipe_by_product_id,
  get_completed_batches_by_recipe,
  get_completed_batches_by_source_recipe,
  get_recipe_process_details,
  get_roles,
  bp_batch_configuration_pharma,
  get_process_control_parameter,
} from '../../../utils/api';
import { useSelector, useDispatch } from 'react-redux';
import DualESignatureAuth from '@/components/DualESignatureAuth';
import { accessControlUtils } from '@/components/AccessControlUtils';
import useAppSetting from '@/hooks/useAppSetting';
const { Text } = Typography;
function Scheduling({
  refresh,
  set_refresh,
  selectedButton,
  setIsBomVisible,
  set_view_bom_data,
  setMinMaxBatchSize,
  setBatchSize,
  batch_type,
  masterBatch,
  selectedLots,
  setSelectedLots,
  onSuccess,
}) {
  const [recipe_data, set_recipe_data] = useState([]);
  const [machine_data, set_machine_data] = useState([]);
  const [selectedMachineKeys, setSelectedMachineKeys] = useState([]);
  const [batch_list, set_batch_list] = useState([]);
  const [product_list, set_product_list] = useState([]);
  const [selected_recipe, set_selected_recipe] = useState();
  // What we are scheduling: 'BMR' (manufacturing, default) or 'BPR' (packaging).
  const [scheduleMode, setScheduleMode] = useState('BMR');
  // For BPR: completed batches of the selected packaging recipe's SOURCE MBMR,
  // shown in the "Source Master Batch" dropdown.
  const [sourceBatchList, setSourceBatchList] = useState([]);
  const [role_list, set_role_list] = useState([]);
  // All Control & Monitoring Parameters configured across every machine in
  // Add Equipment (not filtered to one machine) — shown when Continuous
  // Monitoring is toggled on for this batch release.
  const [monitoring_parameter_list, set_monitoring_parameter_list] = useState([]);
  const [batchNumberLoading, setBatchNumberLoading] = useState(false);
  // E-Signature state
  const [showESignatureModal, setShowESignatureModal] = useState(false);
  const [pendingBatchData, setPendingBatchData] = useState(null);
  const isESign = accessControlUtils('ESign')?.create_access || false;
  const isMasterLotVisible = useAppSetting('Master/Lot Visibility', true);
  const isContinuousMonitoringVisible = useAppSetting(
    'Continuous Monitoring Visibility',
    true
  );
  // When enabled, the auto-generated batch number can be edited on this page.
  const isBatchNumberEditable = useAppSetting('Batch Number Editable', false);

  const selectedModule = useSelector((state) => state.moduleView);
  const [initial_values, set_initial_values] = useState({
    master_lot: false,
    product: '',
    recipe_id: masterBatch.recipe_id,
    batch: '',
    // BPR only: chosen source-MBMR completed batch this packaging batch packs.
    source_master_batch: null,
    // schedule_time: "",
    batch_size: '',
    machine_id: [],
    sub_machine_ids: [],
    machine_data: [],
    batch_type: batch_type,
    continuous_monitoring: false,
    continuous_monitoring_parameter_ids: [],
    selectedModule: selectedModule,
    min_quantity: selected_recipe?.min_quantity || 0,
    max_quantity: selected_recipe?.max_quantity || 0,
    remainning_size: masterBatch?.remainning_size || 0,
  });
  // Initial form values

  //############### Handle API's ##################
  // Machine list is loaded on recipe selection, not on mount

  useEffect(() => {
    const recipeData = recipe_data?.find((item) => item?.recipe_id === selected_recipe?.recipe_id);
    set_selected_recipe(recipeData || null);
    if (recipeData) {
      setMinMaxBatchSize({
        min: recipeData?.min_quantity,
        max: recipeData?.max_quantity,
        actual: recipeData?.master_batch_size,
      });
    }

  }, [selected_recipe?.recipe_id]);

  // recipe_type reflects the Test/Production toggle in the main header:
  // true when Production is selected, false for Test. Sent on every /batches API.
  // Sent as string 'true' (Production) / 'false' (Test) — backend parses it.
  const recipeTypeFlag = selectedButton === 'production';

  const get_master_batchs_data = async () => {
    try {
      await get_master_batchs({ recipe_type: recipeTypeFlag }, (res) => {
        if (res?.data.status === 'success') {
          set_batch_list(res?.data?.data);
        } else {
          set_batch_list([]);
        }
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const success_get_roles = (res) => {
    set_role_list(res.data.data);
  };

  useEffect(() => {
    get_roles(success_get_roles);
    // No machine id passed => every Control & Monitoring Parameter across
    // every machine created in Add Equipment.
    get_process_control_parameter((res) => {
      set_monitoring_parameter_list(res?.data?.data || []);
    });
  }, []);

  const get_recipe = async () => {
    try {
      await get_product_list_both({ module_type: selectedModule, recipe_type: recipeTypeFlag }, success_bp_product_data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const get_recipe_by_productid = (product) => {
    try {
      get_recipe_by_product_id(success_recipe_by_product, {
        product_id: product,
        recipe_type: recipeTypeFlag,
      });
    } catch (error) {
      console.log(error);
    }
  };

  // Load the completed batches of an upstream recipe so the user can pick
  // one — for BPR, the packaging recipe's OWN recipe_id is sent to
  // get_completed_batches_by_source_recipe, which resolves that recipe's
  // source MBMR internally on the backend; for an input-based recipe it's
  // the upstream recipe selected via "Select Recipe" in its metadata
  // (source_mbmr_id), sent to get_completed_batches_by_recipe. Same picker,
  // same source_master_batch field either way.
  const loadSourceBatches = (recipeData) => {
    if (scheduleMode === 'BPR') {
      if (!recipeData?.recipe_id) {
        setSourceBatchList([]);
        return;
      }
      get_completed_batches_by_source_recipe((res) => {
        if (res?.data?.status === 'success') {
          setSourceBatchList(res?.data?.data || []);
        } else {
          // Covers every documented failure case: recipe_id missing (400),
          // recipe not found (404), this recipe has no source_mbmr configured
          // (400), unauthenticated (401 — different shape, already normalized
          // by the api.js wrapper), and unexpected server errors (500).
          setSourceBatchList([]);
          toast.error(
            res?.data?.message || 'Failed to load completed batches for the source recipe'
          );
        }
      }, { recipe_id: recipeData.recipe_id, recipe_type: recipeTypeFlag });
      return;
    }
    const sourceMbmrId = recipeData?.source_mbmr_id;
    if (!sourceMbmrId) {
      setSourceBatchList([]);
      return;
    }
    get_completed_batches_by_recipe((res) => {
      const rows = res?.data?.status === 'success' ? (res?.data?.data || []) : [];
      setSourceBatchList(rows);
    }, { recipe_id: sourceMbmrId, recipe_type: recipeTypeFlag });
  };

  // Recipes shown in the dropdown: for BPR only packaging MBMRs (is_packaging),
  // for BMR only manufacturing MBMRs (non-packaging).
  const visibleRecipes = (recipe_data || []).filter((r) =>
    scheduleMode === 'BPR' ? r.is_packaging : !r.is_packaging
  );

  // Whenever the "Source Master Batch" picker is shown (same condition as
  // that Col below), Batch Size isn't something to type — it's exactly the
  // yield of whichever completed upstream batch gets picked there.
  const sourceBatchDriven =
    (scheduleMode === 'BPR' || selected_recipe?.is_input_based_batch_size) && batch_type !== 'lot';

  const fetchBatchNumber = (recipe_id, setFieldValue) => {
    if (!recipe_id) return;
    const token = localStorage.getItem('Token');
    setBatchNumberLoading(true);
    Axios.get('/bp/generate_batch_number', {
      params: { recipe_id, recipe_type: recipeTypeFlag },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res?.data?.status === 'success') {
          setFieldValue('batch', res.data.data.batch_number);
        } else {
          toast.error(res?.data?.message || 'Failed to generate batch number');
        }
      })
      .catch(() => toast.error('Failed to generate batch number'))
      .finally(() => setBatchNumberLoading(false));
  };

  const getmachines = async (recipe_id) => {
    try {
      const params = recipe_id
        ? { recipe_id, recipe_type: recipeTypeFlag }
        : { recipe_type: recipeTypeFlag };
      await get_api_function(
        '/get_machine_list_with_submachines',
        success_bp_machine_data,
        params
      );
    } catch (error) {
      console.log(error);
    }
  };

  const success_bp_machine_data = (res) => {
    if (res.data.status !== 'failed') {
      const raw = (res?.data?.data || []).map((m) => ({
        ...m,
        machine_name: m.machine_name || m.name,
      }));
      // Sub-machines appear both as children AND as flat items — keep only top-level ones
      const subIds = new Set(raw.flatMap((m) => (m.sub_machines || []).map((s) => s.machine_id)));
      set_machine_data(raw.filter((m) => !subIds.has(m.machine_id)));
    }
  };

  const getDropDownData = async () => {
    try {
      await get_recipe_for_schedule({ module_type: selectedModule, recipe_type: recipeTypeFlag }, success_recipe_list_data);
    } catch (error) {
      console.log(error);
    }
  };

  const success_recipe_list_data = (res) => {
    if (res.data.status !== 'failed') {
      set_recipe_data(res?.data?.data);
    } else {
      set_recipe_data([]);
    }
  };

  const handleViewBom = (recipe_id, batch_size) => {
    if (batch_size && batch_size > 0) {
      setIsBomVisible(true);
      set_view_bom_data({ recipe_id: recipe_id, batch_size: batch_size });
    } else {
      toast.warning('Please enter batch size to view BOM');
    }
  };

  const success_recipe_by_product = (res) => {
    if (res?.data?.status === 'success') {
      set_recipe_data(res?.data?.data);
    } else {
      set_recipe_data([]);
    }
  };

  const success_bp_product_data = (res) => {
    set_product_list(res?.data?.data);
  };

  const get_recipe_process_details_data = async (recipe_id, setFieldValue, values) => {
    try {
      await get_recipe_process_details({ recipe_id: recipe_id, recipe_type: recipeTypeFlag }, (res) => {
        if (res?.data?.status === 'success') {
          const newProcessList = res?.data?.data || [];

          // Preserve already selected roles if process_id still exists
          const updatedRoles = (values.process_roles || []).filter((role) =>
            newProcessList.some((proc) => proc.process_id === role.process_id)
          );

          setFieldValue('process_list_data', newProcessList);
          setFieldValue('process_roles', updatedRoles);
        } else {
          setFieldValue('process_list_data', []);
          setFieldValue('process_roles', []); // reset only when no process list
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    console.log(isESign);

    try {
      if (isESign) {
        setPendingBatchData({ values, resetForm });
        // Show e-signature modal
        setShowESignatureModal(true);
      } else {
        setPendingBatchData(null);
        submitBatchAfterSignature({ values, resetForm });
      }
    } catch (error) {
      console.error('Error preparing batch submission:', error);
    }
  };

  // Submit batch after successful e-signature
  const submitBatchAfterSignature = async (submitData) => {
    console.log(submitData);

    try {
      if (pendingBatchData || submitData) {
        const { values, resetForm } = pendingBatchData || submitData;
        console.log('check');

        if (batch_type === 'lot') {
          const payload = {
            ...values,
            batch_number: masterBatch?.batch_number,
            lot_number: values?.batch,
            lot_quantity: values?.batch_size,
            recipe_id: masterBatch?.recipe_id,
            selected_lots: selectedLots || {},
          };
          batch_configuration_combine(payload, (res) =>
            success_bp_batch_configuration(res, resetForm)
          );
        } else {
          const allMachineIds = [
            ...(values.machine_id || []),
            ...(values.sub_machine_ids || []),
          ].map(Number);
          const payload = {
            ...values,
            machine_id: [...new Set(allMachineIds)],
            batch_number: values.batch,
            selected_lots: selectedLots || {},
            // BPR scheduling: flag + the source-MBMR batch this packages.
            // Same source_master_batch field also carries an input-based
            // recipe's selected upstream batch (see Source Master Batch above).
            schedule_mode: scheduleMode,
            is_packaging: scheduleMode === 'BPR',
            source_master_batch:
              scheduleMode === 'BPR' || selected_recipe?.is_input_based_batch_size
                ? (values.source_master_batch || null)
                : null,
            // Test/Production toggle from the main header.
            recipe_type: recipeTypeFlag,
          };
          delete payload.sub_machine_ids;
          delete payload.machine_data;
          bp_batch_configuration_pharma(payload, (res) =>
            success_bp_batch_configuration(res, resetForm)
          );
        }
      } else {
        return;
      }

      // Clear pending data
      setPendingBatchData(null);
    } catch (error) {
      console.error('Error submitting batch after signature:', error);
      toast.error('Failed to submit batch. Please try again.');
    }
  };

  const formikRef = useRef();

  useEffect(() => {
    if (formikRef.current && masterBatch) {
      get_recipe_process_details_data(
        masterBatch.recipe_id,
        formikRef.current.setFieldValue,
        formikRef.current.values
      );
    } else {
      set_initial_values({
        master_lot: false,
        product: '',
        recipe_id: '',
        batch: '',
        batch_size: '',
        machine_id: [],
        sub_machine_ids: [],
        machine_data: [],
        batch_type: batch_type,
        selectedModule: null,
        min_quantity: 0,
        continuous_monitoring: false,
        continuous_monitoring_parameter_ids: [],
        max_quantity: 0,
        remainning_size: 0,
        // process_roles: [],
        // process_list_data: [],
      });
    }
  }, [masterBatch]);

  const success_bp_batch_configuration = (res, resetForm) => {
    if (res.status === 'failed') {
      toast.warning(res.message);
    } else {
      toast.success('Batch added Successfully');
      resetForm();
      setSelectedMachineKeys([]);
      if (setSelectedLots) setSelectedLots({});
      set_refresh(!refresh);
      if (onSuccess) onSuccess();
    }
  };

  useEffect(() => {
    get_recipe();
    get_master_batchs_data();
    if (selectedModule === 'pack') {
      getDropDownData();
    } else {
      set_recipe_data([]);
      set_product_list([]);
    }
    // Re-fetch when the Test/Production toggle changes so recipe_type is applied.
  }, [selectedModule, selectedButton]);

  useEffect(() => {
    if (batch_type === 'lot' && masterBatch?.recipe_id && formikRef?.current) {
      fetchBatchNumber(masterBatch.recipe_id, formikRef.current.setFieldValue);
    }
  }, [batch_type, masterBatch?.recipe_id]);

  return (
    <div>
      <Formik
        enableReinitialize
        innerRef={formikRef}
        initialValues={initial_values}
        validationSchema={
          batch_type === 'lot'
            ? Yup.object().shape({
              batch: Yup.string().required('Batch/Lot Number is required'),
              batch_size: Yup.number()
                .nullable() // allow null initially
                .typeError('Lot Size must be a number')
                .required('Lot Size is required')
                .min(1, 'Lot Size must be greater than 0')
                .max(masterBatch?.remainning_size || 0, `Lot Size cannot exceed remaining size`),
              continuous_monitoring_parameter_ids: Yup.array().when('continuous_monitoring', {
                is: true,
                then: (s) => s.min(1, 'Select at least one monitoring parameter'),
                otherwise: (s) => s.nullable(),
              }),
              // process_roles: Yup.array().when("master_lot", {
              //   is: false,
              //   then: Yup.array()
              //     .of(
              //       Yup.object().shape({
              //         process_id: Yup.number().required(),
              //         role_id: Yup.array()
              //           .of(Yup.string().required("Invalid role"))
              //           .min(
              //             1,
              //             "At least one role is required for this process"
              //           )
              //           .required("Role is required for this process"),
              //       })
              //     )
              //     .test(
              //       "all-processes-assigned",
              //       "All processes must have assigned roles",
              //       function (value) {
              //         const { process_list_data } = this.parent;
              //         if (
              //           !process_list_data ||
              //           process_list_data.length === 0
              //         )
              //           return true;

              //         const assignedProcessIds = (value || []).map(
              //           (item) => item.process_id
              //         );
              //         const allProcessIds = process_list_data.map(
              //           (process) => process.process_id
              //         );

              //         return allProcessIds.every((processId) =>
              //           assignedProcessIds.includes(processId)
              //         );
              //       }
              //     ),
              //   otherwise: Yup.array(),
              // }),
            })
            : Yup.object().shape({
              product: Yup.string()
                .nullable()
                .when('selectedModule', {
                  is: 'prod',
                  then: Yup.string().required('Product is required'),
                  otherwise: Yup.string(),
                }),
              recipe_id: Yup.string().nullable().required('Recipe is required'),
              batch: Yup.string().required('Batch/Lot Number is required'),
              // schedule_time: Yup.string().nullable().required("Schedule Time is required"),
              // machine_id: Yup.string()
              //   .nullable()
              //   .when("batch_type", {
              //     is: (val) => val !== "lot",
              //     then: Yup.string(),
              //     otherwise: Yup.string(),
              //   }),
              batch_size: Yup.number()
                .nullable()
                .typeError('Batch Size must be a number')
                .required('Batch Size is required')
                .min(1, 'Batch Size must be greater than 0'),

              // process_roles: Yup.array().when("master_lot", {
              //   is: false,
              //   then: Yup.array()
              //     .of(
              //       Yup.object().shape({
              //         process_id: Yup.number().required(),
              //         role_id: Yup.array()
              //           .of(Yup.string().required("Invalid role"))
              //           .min(
              //             1,
              //             "At least one role is required for this process"
              //           )
              //           .required("Role is required for this process"),
              //       })
              //     )
              //     .test(
              //       "all-processes-assigned",
              //       "All processes must have assigned roles",
              //       function (value) {
              //         const { process_list_data } = this.parent;
              //         if (
              //           !process_list_data ||
              //           process_list_data.length === 0
              //         )
              //           return true;

              //         const assignedProcessIds = (value || []).map(
              //           (item) => item.process_id
              //         );
              //         const allProcessIds = process_list_data.map(
              //           (process) => process.process_id
              //         );

              //         return allProcessIds.every((processId) =>
              //           assignedProcessIds.includes(processId)
              //         );
              //       }
              //     ),
              //   otherwise: Yup.array(),
              // }),
              continuous_monitoring_parameter_ids: Yup.array().when('continuous_monitoring', {
                is: true,
                then: (s) => s.min(1, 'Select at least one monitoring parameter'),
                otherwise: (s) => s.nullable(),
              }),
            })
        }
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, setFieldValue, handleSubmit, errors, touched, handleReset }) => (
          <Form layout="vertical">
            {/* Schedule type: BMR (manufacturing, default) vs BPR (packaging).
                Two selectable cards — the active one is filled teal with a check,
                so the current choice is unmistakable. Hidden for lot scheduling. */}
            {batch_type !== 'lot' && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Release Type
                </div>
                <div style={{ display: 'inline-flex', gap: 8 }}>
                  {[
                    { value: 'BMR', title: 'BMR', sub: 'Manufacturing', Icon: Factory },
                    { value: 'BPR', title: 'BPR', sub: 'Packaging', Icon: Package },
                  ].map(({ value, title, sub, Icon }) => {
                    const active = scheduleMode === value;
                    return (
                      <div
                        key={value}
                        role="button"
                        onClick={() => {
                          if (active) return;
                          setScheduleMode(value);
                          // Reset recipe-dependent selections on mode switch.
                          setFieldValue('recipe_id', null);
                          setFieldValue('source_master_batch', null);
                          set_selected_recipe(undefined);
                          setSourceBatchList([]);
                          setFieldValue('batch', '');
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 12px',
                          borderRadius: 8,
                          cursor: active ? 'default' : 'pointer',
                          userSelect: 'none',
                          border: `1.5px solid ${active ? '#204448' : '#e5e7eb'}`,
                          background: active ? '#204448' : '#ffffff',
                          color: active ? '#ffffff' : '#374151',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Icon size={16} color={active ? '#ffffff' : '#204448'} />
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
                        <span style={{ fontSize: 11, opacity: active ? 0.8 : 0.55 }}>{sub}</span>
                        {active && <Check size={14} color="#ffffff" strokeWidth={3} style={{ marginLeft: 2 }} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <Row gutter={16}>
              {batch_type === 'lot' && (
                <Col span={6}>
                  <Form.Item
                    label="Master Batch"
                    validateStatus={touched.master_batch && errors.master_batch ? 'error' : ''}
                    help={touched.master_batch && errors.master_batch ? errors.master_batch : ''}
                  >
                    {/* <Select
                      showSearch
                      allowClear
                      disabled={batch_type === "lot"}
                      size="large"
                      placeholder="Select Batch"
                      value={values.master_batch || null}
                      onChange={(value) => {
                        setFieldValue("master_batch", value);
                        if (value) {
                          let val_name = value?.split("_");
                          const masterBatchData = batch_list?.find(
                            (mdata) => mdata?.batch_number === value
                          );
                          set_selected_master_batch_data(masterBatchData);
                          get_recipe_by_productid(masterBatchData?.product_id);
                          setFieldValue("product", masterBatchData?.product_id);
                          setFieldValue(
                            "recipe_id",
                            masterBatchData?.recipe_id
                          );
                          setFieldValue(
                            "batch",
                            `${val_name[val_name?.length - 1]
                              ?.trim()
                              ?.replace(/[^a-zA-Z0-9/_]/g, "")}_`
                          );
                        } else {
                          handleReset();
                        }
                      }}
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                    >
                      {batch_list?.map((mdata) => (
                        <Select.Option
                          key={mdata?.batch_number}
                          value={mdata?.batch_number}
                        >
                          {mdata.batch_number}
                        </Select.Option>
                      ))}
                    </Select> */}
                    <Input
                      placeholder="Master Lot"
                      disabled
                      value={masterBatch?.batch_number || ''}
                      style={{ backgroundColor: '#f5f5f5' }}
                      size="large"
                    />
                  </Form.Item>
                </Col>
              )}

              {selectedModule === 'prod' && (
                <Col span={6}>
                  <Form.Item
                    label={selectedModule === 'prod' ? 'Product' : 'MBMR Product'}
                    validateStatus={touched.product && errors.product ? 'error' : ''}
                    help={touched.product && errors.product ? errors.product : ''}
                  >
                    {batch_type === 'lot' ? (
                      <Input
                        placeholder="Product"
                        disabled
                        value={masterBatch?.product_name || ''}
                        style={{ backgroundColor: '#f5f5f5' }}
                        size="large"
                      />
                    ) : (
                      <Select
                        showSearch
                        size="large"
                        placeholder="Select Product"
                        value={values.product || null}
                        onChange={(value) => {
                          setFieldValue('product', value);
                          setFieldValue('recipe_id', '');
                          get_recipe_by_productid(value);
                        }}
                        disabled={batch_type === 'lot'}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().includes(input?.toLowerCase())
                        }
                        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                      >
                        {product_list?.map((mdata) => (
                          <Select.Option key={mdata.product_id} value={mdata.product_id}>
                            {mdata.product_name}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              )}

              <Col span={6}>
                <Form.Item
                  label="MBMR"
                  validateStatus={touched.recipe_id && errors.recipe_id ? 'error' : ''}
                  help={touched.recipe_id && errors.recipe_id ? errors.recipe_id : ''}
                >
                  {batch_type === 'lot' ? (
                    <Input
                      placeholder="MBMR"
                      disabled
                      size="large"
                      value={masterBatch?.recipe_name || ''}
                      style={{ backgroundColor: '#f5f5f5' }}
                    />
                  ) : (
                    <Select
                      showSearch
                      allowClear
                      size="large"
                      placeholder="Select MBMR"
                      value={values.recipe_id || null}
                      onChange={(value) => {
                        setFieldValue('recipe_id', value);
                        setFieldValue('machine_data', []);
                        setSelectedMachineKeys([]);
                        set_machine_data([]);
                        const recipeData = recipe_data?.find((mdata) => mdata?.recipe_id === value);
                        set_selected_recipe(recipeData);
                        get_recipe_process_details_data(value, setFieldValue, values);
                        // Load machines filtered by this recipe's configured areas
                        if (value) {
                          getmachines(value);
                        }
                        // Fetch batch number from backend API
                        fetchBatchNumber(value, setFieldValue);
                        const needsSourceBatch =
                          (scheduleMode === 'BPR' || recipeData?.is_input_based_batch_size) &&
                          batch_type !== 'lot';
                        if (needsSourceBatch) {
                          // Batch Size will come from whichever source master
                          // batch gets picked below — clear the stale value
                          // rather than showing the recipe's own (disabled,
                          // now-irrelevant) master batch size.
                          setFieldValue('batch_size', '');
                          setBatchSize('');
                        } else if (recipeData?.master_batch_size) {
                          // Default batch size to recipe's actual quantity when recipe is selected
                          setFieldValue('batch_size', recipeData.master_batch_size);
                          setBatchSize(recipeData.master_batch_size);
                        }
                        // Load completed batches of the upstream recipe for the
                        // Source Master Batch picker — BPR (packaging's source
                        // MBMR) or an input-based recipe's own "Select Recipe".
                        setFieldValue('source_master_batch', null);
                        if (needsSourceBatch) {
                          loadSourceBatches(recipeData);
                        }
                      }}
                      disabled={batch_type === 'lot'}
                      filterOption={(input, option) => {
                        // Grouped options have no `children` string; match on the
                        // per-option label we stash in `data-label` instead.
                        const label = option?.['data-label'] ?? option?.children;
                        return String(label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase());
                      }}
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    >
                      {(() => {
                        // Group recipes that belong to a version family (share a
                        // version_root_id) under an OptGroup labelled with the family
                        // (root recipe) name, ordered by version_number. Recipes with
                        // no version lineage render as plain top-level options.
                        const families = {};
                        const standalone = [];
                        (visibleRecipes || []).forEach((r) => {
                          if (r?.version_root_id) {
                            (families[r.version_root_id] ||= {
                              name: r.version_root_name || 'Versions',
                              items: [],
                            }).items.push(r);
                          } else {
                            standalone.push(r);
                          }
                        });
                        const renderOption = (mdata) => (
                          <Select.Option
                            key={mdata?.recipe_id}
                            value={mdata?.recipe_id}
                            data-label={mdata?.recipe_name}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {mdata.recipe_name}
                              {mdata?.version_number ? (
                                <Tag color="blue" style={{ marginInlineEnd: 0, lineHeight: '18px' }}>
                                  v{mdata.version_number}
                                </Tag>
                              ) : null}
                            </span>
                          </Select.Option>
                        );
                        return (
                          <>
                            {standalone.map(renderOption)}
                            {Object.entries(families).map(([rootId, fam]) => (
                              <Select.OptGroup key={`fam-${rootId}`} label={fam.name}>
                                {fam.items
                                  .slice()
                                  .sort((a, b) => (a.version_number || 0) - (b.version_number || 0))
                                  .map(renderOption)}
                              </Select.OptGroup>
                            ))}
                          </>
                        );
                      })()}
                    </Select>
                  )}
                </Form.Item>
              </Col>

              {/* Source Master Batch — BPR (packaging's source MBMR) or an
                  input-based recipe (the recipe picked via "Select Recipe" in
                  its metadata). Either way: lists completed batches of that
                  upstream recipe — the batch this one packs/consumes. */}
              {(scheduleMode === 'BPR' || selected_recipe?.is_input_based_batch_size) && batch_type !== 'lot' && (
                <Col span={6}>
                  <Form.Item label="Source Master Batch">
                    <Select
                      showSearch
                      allowClear
                      size="large"
                      placeholder="Select source master batch"
                      value={values.source_master_batch || null}
                      onChange={(val) => {
                        setFieldValue('source_master_batch', val || null);
                        // Batch Size isn't a number to type here — it's exactly
                        // the yield of whichever completed upstream batch gets
                        // picked, so pull it straight from that batch.
                        const picked = (sourceBatchList || []).find((b) => b.batch_number === val);
                        setFieldValue('batch_size', picked?.batch_size ?? '');
                        setBatchSize(picked?.batch_size ?? '');
                      }}
                      disabled={!values.recipe_id}
                      notFoundContent={
                        !values.recipe_id
                          ? 'Select a recipe first'
                          : !selected_recipe?.source_mbmr_id
                            ? 'No upstream recipe configured for this recipe'
                            : 'No completed batches for the upstream recipe'
                      }
                      filterOption={(input, option) =>
                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    >
                      {(sourceBatchList || []).map((b) => (
                        <Select.Option key={b.batch_number} value={b.batch_number}>
                          {b.batch_number}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}

              <Col span={6}>
                <Form.Item
                  label={batch_type === 'master' ? 'Batch' : 'Lot Number'}
                  validateStatus={touched.batch && errors.batch ? 'error' : ''}
                  help={touched.batch && errors.batch ? errors.batch : ''}
                >
                  <Input
                    placeholder={batch_type === 'lot' ? 'Lot Number' : 'Batch'}
                    value={values.batch}
                    size="large"
                    // Editable only for master batches when the "Batch Number
                    // Editable" application setting is enabled; otherwise the
                    // auto-generated value is locked. Lot numbers stay locked.
                    disabled={!(isBatchNumberEditable && batch_type !== 'lot')}
                    onChange={(e) => setFieldValue('batch', e.target.value)}
                    style={
                      isBatchNumberEditable && batch_type !== 'lot'
                        ? undefined
                        : { backgroundColor: '#f5f5f5', color: '#333', cursor: 'not-allowed' }
                    }
                  />
                </Form.Item>
              </Col>

              {/* <Col span={8}>
                <Form.Item
                  label="Schedule Time"
                  validateStatus={
                    errors.schedule_time && touched.schedule_time ? "error" : ""
                  }
                  help={
                    errors.schedule_time && touched.schedule_time
                      ? errors.schedule_time
                      : null
                  }
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    needConfirm={false}
                    value={
                      values.schedule_time ? dayjs(values.schedule_time) : null
                    }
                    onChange={(value) => {
                      setFieldValue(
                        "schedule_time",
                        value ? value.toISOString() : null
                      );
                    }}
                    style={{ width: "100%" }}
                    size="large"
                    disabledDate={(current) =>
                      current && current < dayjs().startOf("day")
                    }
                  />
                </Form.Item>
              </Col> */}

              {values.recipe_id && <Col span={6}>
                <Form.Item
                  label="Machine"
                  validateStatus={touched.machine_id && errors.machine_id ? 'error' : ''}
                  help={touched.machine_id && errors.machine_id ? errors.machine_id : ''}
                >
                  <TreeSelect
                    placeholder="Select Machine(s) & Submachines"
                    size="large"
                    allowClear
                    multiple
                    treeCheckable
                    treeCheckStrictly
                    showCheckedStrategy={TreeSelect.SHOW_ALL}
                    treeDefaultExpandAll={false}
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    maxTagCount={selectedMachineKeys.includes('__all__') ? undefined : 'responsive'}
                    treeNodeFilterProp="title"
                    tagRender={(props) => {
                      const allSelected = selectedMachineKeys.includes('__all__');
                      // When All is selected, suppress every individual machine tag
                      if (allSelected && props.value !== '__all__') return null;
                      const isAll = props.value === '__all__';
                      return (
                        <span
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '0 4px 0 8px', margin: '2px 4px 2px 0',
                            lineHeight: '22px', borderRadius: 4, fontSize: 12,
                            background: isAll ? 'rgba(32,68,72,0.1)' : '#f5f5f5',
                            border: `1px solid ${isAll ? 'rgba(32,68,72,0.3)' : '#d9d9d9'}`,
                          }}
                        >
                          <span style={{ fontWeight: isAll ? 700 : 400, color: isAll ? '#204448' : undefined }}>
                            {isAll ? 'All' : props.label}
                          </span>
                          {props.closable && (
                            <span
                              onClick={(e) => { e.stopPropagation(); props.onClose(); }}
                              style={{ cursor: 'pointer', padding: '0 2px', opacity: 0.6, fontSize: 14 }}
                            >
                              ×
                            </span>
                          )}
                        </span>
                      );
                    }}
                    value={selectedMachineKeys.map((k) => ({ value: k, label: k }))}
                    treeData={[
                      {
                        title: <span style={{ fontWeight: 600, color: '#204448' }}>All</span>,
                        value: '__all__',
                        key: '__all__',
                      },
                      ...(machine_data || []).map((machine) => ({
                        title: machine?.machine_name || machine?.name,
                        value: `m-${machine?.machine_id}`,
                        key: `m-${machine?.machine_id}`,
                        children: (machine?.sub_machines || []).map((sub) => ({
                          title: sub?.name || `Machine #${sub?.machine_id}`,
                          value: `s-${sub?.machine_id}`,
                          key: `s-${sub?.machine_id}`,
                        })),
                      })),
                    ]}
                    onChange={(selected) => {
                      // treeCheckStrictly returns [{value, label}] — normalise to plain strings
                      const selectedKeys = (selected || []).map((s) => (typeof s === 'object' ? s.value : s));
                      const machines = machine_data || [];
                      const topLevelKeys = machines.map((machine) => `m-${machine.machine_id}`);
                      const subKeysByMain = new Map(
                        machines.map((machine) => [
                          `m-${machine.machine_id}`,
                          (machine.sub_machines || []).map((sub) => `s-${sub.machine_id}`),
                        ])
                      );
                      const allSubKeys = [...new Set(
                        [...subKeysByMain.values()].flat()
                      )];
                      const wasAll = selectedMachineKeys.includes('__all__');
                      const isAll = selectedKeys.includes('__all__');

                      let finalKeys;

                      if (!wasAll && isAll) {
                        // Selecting "All" selects every main machine and mapped submachine.
                        finalKeys = ['__all__', ...topLevelKeys, ...allSubKeys];
                      } else if (wasAll && !isAll) {
                        // Unchecked "All" → deselect everything
                        finalKeys = [];
                      } else {
                        const previousKeys = new Set(
                          selectedMachineKeys.filter((key) => key !== '__all__')
                        );
                        const nextKeys = new Set(
                          selectedKeys.filter((key) => key !== '__all__')
                        );

                        topLevelKeys.forEach((mainKey) => {
                          const wasSelected = previousKeys.has(mainKey);
                          const isSelected = nextKeys.has(mainKey);

                          if (!wasSelected && isSelected) {
                            // A newly selected main starts with all its children selected.
                            (subKeysByMain.get(mainKey) || []).forEach((subKey) => {
                              nextKeys.add(subKey);
                            });
                          } else if (wasSelected && !isSelected) {
                            // Keep a shared submachine when another selected main still owns it.
                            (subKeysByMain.get(mainKey) || []).forEach((subKey) => {
                              const belongsToAnotherSelectedMain = topLevelKeys.some(
                                (otherMainKey) =>
                                  otherMainKey !== mainKey &&
                                  nextKeys.has(otherMainKey) &&
                                  (subKeysByMain.get(otherMainKey) || []).includes(subKey)
                              );
                              if (!belongsToAnotherSelectedMain) nextKeys.delete(subKey);
                            });
                          }
                        });

                        const realKeys = [...nextKeys];
                        const allSelected =
                          topLevelKeys.length > 0 &&
                          topLevelKeys.every((key) => nextKeys.has(key)) &&
                          allSubKeys.every((key) => nextKeys.has(key));
                        finalKeys = allSelected ? ['__all__', ...realKeys] : realKeys;
                      }

                      finalKeys = [...new Set(finalKeys)];
                      setSelectedMachineKeys(finalKeys);

                      const keyList = finalKeys.filter((k) => k !== '__all__');
                      const machineIds = [...new Set(
                        keyList
                          .filter((key) => key.startsWith('m-'))
                          .map((key) => Number(key.slice(2)))
                      )];
                      const subMachineIds = [...new Set(
                        keyList
                          .filter((key) => key.startsWith('s-'))
                          .map((key) => Number(key.slice(2)))
                      )];

                      const combined = machineIds.map((mainId) => {
                        const mainMachine = (machine_data || []).find(
                          (m) => m?.machine_id === mainId
                        );
                        const validSubIds = (mainMachine?.sub_machines || [])
                          .map((s) => s?.machine_id)
                          .filter((sId) => subMachineIds.includes(sId));
                        return { machine_id: mainId, sub_machine_ids: validSubIds };
                      });

                      setFieldValue('machine_id', machineIds);
                      setFieldValue('sub_machine_ids', subMachineIds);
                      setFieldValue('machine_data', combined);
                    }}
                  />
                </Form.Item>
              </Col>}

              <Col span={6}>
                <Form.Item
                  label={batch_type === 'master' ? 'Batch Size' : 'Lot Size'}
                  validateStatus={touched.batch_size && errors.batch_size ? 'error' : ''}
                  help={touched.batch_size && errors.batch_size ? errors.batch_size : ''}
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <InputNumber
                      placeholder={
                        sourceBatchDriven ? 'Yield of the selected source master batch' : 'Enter Batch Size'
                      }
                      style={{ width: '100%' }}
                      value={values.batch_size}
                      onChange={(value) => {
                        setFieldValue('batch_size', value);
                        setBatchSize(value);
                      }}
                      size="large"
                      // Driven entirely by whichever completed upstream batch is
                      // picked in "Source Master Batch" above — not a number to
                      // type here (BPR's packed batch / an input-based recipe's
                      // consumed batch is exactly that batch's own yield).
                      disabled={sourceBatchDriven}
                      min={batch_type === 'lot' ? 0 : undefined}
                      max={
                        batch_type === 'lot' ? masterBatch?.remainning_size || undefined : undefined
                      }
                    />
                    <Button
                      type="primary"
                      disabled={!(values.recipe_id || selected_recipe)}
                      icon={<EyeOutlined />}
                      size="large"
                      onClick={() => handleViewBom(values.recipe_id, values.batch_size)}
                    >
                      View BOM
                    </Button>
                  </Space.Compact>
                  {sourceBatchDriven && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Set from the selected Source Master Batch's yield
                    </Text>
                  )}
                  {!sourceBatchDriven && batch_type === 'master' && selected_recipe && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Master Batch Size: {selected_recipe.master_batch_size ?? '-'}
                    </Text>
                  )}
                  {batch_type === 'lot' && masterBatch?.remainning_size && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Remaining Size: {masterBatch.remainning_size}
                    </Text>
                  )}
                </Form.Item>
              </Col>

              {(isContinuousMonitoringVisible ||
                (isMasterLotVisible && batch_type !== 'lot')) && (
                <Col span={6}>
                  <div className="flex items-center gap-5">
                    {isMasterLotVisible && batch_type !== 'lot' && (
                      <Form.Item
                        label="Master/Lot"
                        validateStatus={touched.master_lot && errors.master_lot ? 'error' : ''}
                        help={touched.master_lot && errors.master_lot ? errors.master_lot : ''}
                      >
                        <Switch
                          checked={values.master_lot}
                          onChange={(value) => {
                            setFieldValue('master_lot', value);
                            // Clear process_roles when switching to master
                            if (value) {
                              setFieldValue('process_roles', []);
                            }
                          }}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                          style={{
                            backgroundColor: values.master_lot ? '#22c55e' : '#d1d5db',
                          }}
                        />
                      </Form.Item>
                    )}
                    {isContinuousMonitoringVisible && (
                      <Form.Item
                        label={<span style={{ whiteSpace: 'nowrap' }}>Continuous Monitoring</span>}
                        validateStatus={
                          touched.continuous_monitoring && errors.continuous_monitoring ? 'error' : ''
                        }
                        help={
                          touched.continuous_monitoring && errors.continuous_monitoring
                            ? errors.continuous_monitoring
                            : ''
                        }
                      >
                        <Switch
                          checked={values.continuous_monitoring}
                          onChange={(value) => {
                            setFieldValue('continuous_monitoring', value);
                            if (!value) setFieldValue('continuous_monitoring_parameter_ids', []);
                          }}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                          style={{
                            backgroundColor: values.continuous_monitoring ? '#22c55e' : '#d1d5db',
                          }}
                        />
                      </Form.Item>
                    )}
                  </div>
                </Col>
              )}

              {/* Monitoring Parameters — every Control & Monitoring Parameter
                  configured across all machines in Add Equipment, not just
                  one machine. Shown only when Continuous Monitoring is on. */}
              {isContinuousMonitoringVisible && values.continuous_monitoring && (
                <Col span={12}>
                  <Form.Item
                    label="Monitoring Parameters"
                    required
                    validateStatus={
                      touched.continuous_monitoring_parameter_ids && errors.continuous_monitoring_parameter_ids
                        ? 'error'
                        : ''
                    }
                    help={
                      touched.continuous_monitoring_parameter_ids && errors.continuous_monitoring_parameter_ids
                    }
                  >
                    <Select
                      mode="multiple"
                      showSearch
                      allowClear
                      size="large"
                      className="monitoring-params-select"
                      style={{ width: '100%' }}
                      placeholder="Select monitoring parameters"
                      optionFilterProp="label"
                      value={values.continuous_monitoring_parameter_ids}
                      notFoundContent="No monitoring parameters configured in Add Equipment"
                      onChange={(val) => setFieldValue('continuous_monitoring_parameter_ids', val)}
                      options={monitoring_parameter_list?.map((p) => ({
                        // Display-only — value (id) sent in the payload is
                        // unchanged. Label is machine_group_name (not the
                        // specific machine_name) : parameter name, e.g.
                        // "Multi Mill : MULTI_MILL_PRESSURE" — disambiguates
                        // parameters that share the same name across
                        // different machine groups.
                        value: p.id,
                        label: `${p.machine_group_name ? `${p.machine_group_name} : ` : ''}${p.name}`,
                      }))}
                    />
                  </Form.Item>
                </Col>
              )}

              {/* Process-Roles Assignment Table */}
              {/* {values.master_lot === false &&
                values.process_list_data?.length > 0 && (
                  <Col span={24}>
                    <Card
                      title="Process Role Assignment"
                      style={{ marginBottom: 16 }}
                      size="small"
                    >
                      <Table
                        columns={getProcessRolesColumns(
                          values,
                          setFieldValue,
                          errors
                        )}
                        dataSource={values.process_list_data}
                        rowKey="process_id"
                        pagination={false}
                        size="small"
                        bordered
                      />
                      {errors.process_roles &&
                        typeof errors.process_roles === "string" && (
                          <Text
                            type="danger"
                            style={{
                              fontSize: "12px",
                              marginTop: 8,
                              display: "block",
                            }}
                          >
                            {errors.process_roles}
                          </Text>
                        )}
                    </Card>
                  </Col>
                )} */}

              <Col span={24}>
                <Form.Item style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                    <Button onClick={() => {
                      handleReset();
                      if (batch_type === 'lot' && masterBatch?.recipe_id) {
                        fetchBatchNumber(masterBatch.recipe_id, setFieldValue);
                      }
                    }}>Reset</Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      Submit
                    </Button>
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>

      {/* E-Signature Modal - Basic + 21 CFR Certificate */}
      <DualESignatureAuth
        visible={showESignatureModal}
        onCancel={() => {
          setShowESignatureModal(false);
          setPendingBatchData(null);
        }}
        onSubmit={(signatureData) => {
          submitBatchAfterSignature();
          setShowESignatureModal(false);
          setPendingBatchData(null);
        }}
        signatureType="batch_schedule"
        action_type="batch_schedule"
        signatureMeaning={batch_type === 'lot' ? 'Lot Scheduling' : 'Batch Scheduling'}
        documentType="BATCH"
        batch_number={pendingBatchData?.values?.batch || ''}
        recipe_id={pendingBatchData?.values?.recipe_id || ''}
        documentId={pendingBatchData?.values?.recipe_id || ''}
        documentNumber={pendingBatchData?.values?.batch || ''}
        documentData={{
          batch_number: pendingBatchData?.values?.batch || '',
          recipe_id: pendingBatchData?.values?.recipe_id || '',
          batch_size: pendingBatchData?.values?.batch_size || 0,
          batch_type: batch_type,
          module: selectedModule,
        }}
        config={{
          enableBasic: true,
          enableCFR: true,
          defaultMode: 'cfr',
          requireComment: true,
          title: 'E-Signature for Batch Scheduling',
        }}
      />

      <style>{`
        .monitoring-params-select .ant-select-selector {
          max-height: 116px;
          overflow-y: auto;
        }
      `}</style>
    </div>
  );
}

export default Scheduling;
